'use client'

import { useMemo } from 'react'
import {
  Editor,
  EditorContent,
  EditorContext,
  EditorContextValue,
  type AnyExtension,
} from '@tiptap/react'
import { cn } from '@/lib/utils'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
// --- UI ---
import {
  useAiEditor,
  type ContentSyncMode,
} from '../hooks/useAiEditor'
// --- React Utils ---
// --- Tiptap Extensions ---
import { AiSuggestionOptions } from '../tiptap-extensions/ai/ai-suggestion'
import '../tiptap-extensions/common/action-pill/action-pill.scss'
import '../tiptap-extensions/common/inline-button/inline-button.scss'
import '../tiptap-extensions/common/inline-image/inline-image.scss'
import '../tiptap-node/code-block-node/code-block-node.scss'
import '../tiptap-node/image-node/image-node.scss'
import '../tiptap-node/list-node/list-node.scss'
import '../tiptap-node/paragraph-node/paragraph-node.scss'
import { DragContextMenu } from '../tiptap-ui/drag-context-menu'
import { EmojiDropdownMenu } from '../tiptap-ui/emoji-dropdown-menu'
import { ActionPillManager } from './components/action-pill-manager'
import { RunbookRefMentionManager } from './components/runbook-ref-mention-manager'
import { AiCommandManager } from './components/ai-command-manager'
import { AICompletionResolver } from './components/ai-selector'
import { ButtonManager } from './components/button-manager'
import { InlineImageManager } from './components/inline-image-manager'
import { LinkManager } from './components/link-manager'
import { NotionToolbarFloating } from './components/notion-like-editor-toolbar-floating'
import { TableBubbleMenu } from './components/TableBubbleMenu'
import { VideoManager } from './components/video-manager'
import './styles/_keyframe-animations.scss'
import './styles/_variables.scss'
import './styles/ai-editor.scss'
import './styles/bubble-menu.scss'
import './styles/comments.css'
import './styles/slash-command.scss'

type TiptapAiEditorProps = {
  content: any
  onCreate?: ({ editor }: { editor: Editor }) => void
  onUpdate?: ({ editor }: { editor: Editor }) => void
  readOnly?: boolean
  /**
   * Review mode: content appears readonly but allows text selection for inline comments.
   * When true, editor is technically editable (for selections) but styled as a review document.
   * Use this for auditor workflows where commenting is needed on submitted evidence.
   */
  reviewMode?: boolean
  suggestionOptions?: AiSuggestionOptions & {
    popoverRef?: React.RefObject<HTMLElement | null>
  }
  completionOptions?: {
    completeResolver: AICompletionResolver
    onContinueInChat?: (context: any) => void
  }
  enableComments?: boolean
  commentActionLabel?: string
  hideCommentComposer?: boolean
  showSelectionToolbar?: boolean
  docId?: string
  workspaceId?: string
  branchId?: string
  changeRequestId?: string
  policySubmissionId?: string
  onCommentsUpdated?: (comments: any[] | Map<string, any>) => void
  onCommentActivated?: (commentId: string | null, comment?: any) => void
  onCommentsDataUpdated?: (comments: Map<string, any> | null) => void
  onCommentsError?: (message: string, error: any) => void
  onContinueInChat?: (context: any) => void
  onAddClaimFromSelection?: (payload: {
    docBlockId: string
    quoteText: string
  }) => void
  extensions?: AnyExtension[]
  onAskVera?: (payload: { quoteText?: string; docBlockId?: string }) => void
  baseExtensions?: AnyExtension[]
  editingTools?: 'default' | 'action-pill-only'
  editorContentClassName?: string // Allow custom className for EditorContent
  stripCommentMarks?: boolean
  documentClassName?: string
  tableBehavior?: 'spreadsheet' | 'document'
  contentSyncMode?: ContentSyncMode
}

export function TiptapAiEditor(props: TiptapAiEditorProps) {
  const {
    content,
    onCreate,
    onUpdate,
    readOnly,
    reviewMode = false,
    suggestionOptions,
    completionOptions,
    enableComments = false,
    commentActionLabel,
    hideCommentComposer = false,
    showSelectionToolbar = true,
    docId,
    workspaceId,
    branchId,
    changeRequestId,
    policySubmissionId,
    onCommentsUpdated,
    onCommentActivated,
    onCommentsDataUpdated,
    onCommentsError,
    onContinueInChat,
    onAddClaimFromSelection,
    extensions = [],
    onAskVera,
    baseExtensions,
    editingTools = 'default',
    editorContentClassName,
    stripCommentMarks = false,
    documentClassName,
    tableBehavior = 'document',
    contentSyncMode = 'reactive',
  } = props

  // Review mode should only override readOnly when inline comments are enabled.
  // Otherwise the document must stay truly read-only.
  const effectiveReadOnly = readOnly && !(reviewMode && enableComments)

  // Derive a single flag that respects readOnly and required ids
  // Comments are enabled in review mode even if readOnly is true
  const commentsEnabled = !!(
    enableComments &&
    docId &&
    workspaceId &&
    (!readOnly || reviewMode)
  )
  const { enabled: commentsEnabledFlag, loading: commentsFlagLoading } =
    useFeatureFlag('comments')

  const commentsConfig = useMemo(() => {
    // Allow comments in review mode even if readOnly is true
    if (!commentsEnabled || !docId || !workspaceId || effectiveReadOnly) {
      return undefined
    }

    return {
      enabled: commentsEnabledFlag && !commentsFlagLoading,
      docId,
      workspaceId,
      branchId,
      changeRequestId,
      policySubmissionId,
      hideAddCommentIndicator: hideCommentComposer,
      onCommentsUpdated,
      onCommentActivated,
      onCommentsDataUpdated,
      onError: onCommentsError,
    }
  }, [
    branchId,
    changeRequestId,
    policySubmissionId,
    hideCommentComposer,
    commentsEnabled,
    commentsEnabledFlag,
    commentsFlagLoading,
    docId,
    onCommentActivated,
    onCommentsDataUpdated,
    onCommentsError,
    onCommentsUpdated,
    effectiveReadOnly,
    workspaceId,
  ])

  const editorRemountKey = commentsEnabled
    ? commentsFlagLoading
      ? 'comments-loading'
      : `comments-ready-${commentsEnabledFlag ? 'on' : 'off'}`
    : 'comments-disabled'

  return (
    <TiptapAiEditorInner
      key={editorRemountKey}
      content={content}
      onCreate={onCreate}
      onUpdate={onUpdate}
      readOnly={effectiveReadOnly}
      reviewMode={reviewMode}
      suggestionOptions={suggestionOptions}
      completionOptions={completionOptions}
      enableComments={enableComments}
      commentActionLabel={commentActionLabel}
      hideCommentComposer={hideCommentComposer}
      showSelectionToolbar={showSelectionToolbar}
      docId={docId}
      workspaceId={workspaceId}
      branchId={branchId}
      changeRequestId={changeRequestId}
      policySubmissionId={policySubmissionId}
      onCommentsUpdated={onCommentsUpdated}
      onCommentActivated={onCommentActivated}
      onCommentsDataUpdated={onCommentsDataUpdated}
      onCommentsError={onCommentsError}
      onContinueInChat={onContinueInChat}
      onAddClaimFromSelection={onAddClaimFromSelection}
      extensions={extensions}
      onAskVera={onAskVera}
      baseExtensions={baseExtensions}
      editingTools={editingTools}
      commentsConfig={commentsConfig}
      editorContentClassName={editorContentClassName}
      stripCommentMarks={stripCommentMarks}
      documentClassName={documentClassName}
      tableBehavior={tableBehavior}
      contentSyncMode={contentSyncMode}
    />
  )
}

type InnerProps = TiptapAiEditorProps & {
  documentClassName?: string
  tableBehavior?: 'spreadsheet' | 'document'
  commentsConfig:
    | {
        enabled: boolean
        docId: string
        workspaceId: string
        branchId?: string
        changeRequestId?: string
        policySubmissionId?: string
        hideAddCommentIndicator?: boolean
        onCommentsUpdated?: (comments: any[] | Map<string, any>) => void
        onCommentActivated?: (commentId: string | null, comment?: any) => void
        onCommentsDataUpdated?: (comments: Map<string, any> | null) => void
        onError?: (message: string, error: any) => void
      }
    | undefined
}

function TiptapAiEditorInner({
  content,
  onCreate,
  onUpdate,
  readOnly,
  reviewMode = false,
  suggestionOptions,
  completionOptions,
  enableComments = false,
  commentActionLabel,
  hideCommentComposer = false,
  showSelectionToolbar = true,
  onContinueInChat,
  onAddClaimFromSelection,
  extensions = [],
  onAskVera,
  baseExtensions,
  editingTools = 'default',
  commentsConfig,
  editorContentClassName,
  stripCommentMarks = false,
  documentClassName,
  tableBehavior = 'document',
  contentSyncMode = 'reactive',
}: InnerProps) {
  const { editor } = useAiEditor({
    content,
    readOnly: !!readOnly,
    reviewMode,
    onCreate,
    onUpdate,
    baseExtensions,
    extensions,
    suggestionOptions,
    commentsConfig,
    stripCommentMarks,
    contentSyncMode,
  })

  if (!editor) {
    return null
  }

  // In review mode, hide editing tools but keep the editor interactive for comments
  const hideEditingTools = readOnly || reviewMode

  return (
    <EditorContext.Provider value={{ editor } as EditorContextValue}>
      <div className='editor-container relative w-full pt-10'>
        <div style={{ display: hideEditingTools ? 'none' : 'block' }}>
          {editingTools === 'default' && (
            <>
              <LinkManager editor={editor} />
              <ButtonManager editor={editor} />
            </>
          )}
          {editingTools === 'default' && (
            <>
              <InlineImageManager editor={editor} />
              <VideoManager editor={editor} />
              <AiCommandManager
                editor={editor}
                completeResolver={completionOptions?.completeResolver}
              />
            </>
          )}
        </div>
        {/* Pill / ref popovers stay mounted in read-only too so click-to-inspect
            works; their anchor needs a visible parent for correct positioning,
            and the components render a read-only variant when readOnly=true. */}
        <ActionPillManager editor={editor} readOnly={hideEditingTools} />
        <RunbookRefMentionManager editor={editor} readOnly={hideEditingTools} />

        <div
          className={cn(
            'editor-content relative w-full bg-card sm:rounded-md',
            tableBehavior === 'document' && 'table-behavior-document',
            documentClassName
          )}
        >
          {!hideEditingTools && editingTools === 'default' && (
            <DragContextMenu editor={editor} />
          )}

          <EditorContent
            editor={editor}
            readOnly={readOnly}
            className={editorContentClassName || 'mt-4 mb-16'}
            style={{
              cursor: editor.view.dragging ? 'grabbing' : 'auto',
            }}
          >
            <EmojiDropdownMenu />
            {/* Selection toolbar. Mounts for any editable default-mode editor
                so plain formatting (bold/italic/…) is always available — the
                AI actions inside (Ask Vera / Add Claim / inline AI selector)
                self-gate on their callbacks/resolver, so editors without AI
                wiring (e.g. the vault draft) still get the formatting menu.
                `action-pill-only` editors (runbook) stay minimal. */}
            {showSelectionToolbar &&
              !hideEditingTools &&
              editingTools === 'default' && (
                <NotionToolbarFloating
                  completeResolver={completionOptions?.completeResolver}
                  enableComments={enableComments}
                  commentActionLabel={commentActionLabel}
                  onContinueInChat={
                    onContinueInChat || completionOptions?.onContinueInChat
                  }
                  onAddClaimFromSelection={onAddClaimFromSelection}
                  onAskVera={onAskVera}
                />
              )}
            {/* In review / read mode, show the selection toolbar without
                formatting marks: inline comment plus Ask Vera / Add Claim
                when wired. */}
            {showSelectionToolbar &&
              reviewMode &&
              enableComments &&
              !hideCommentComposer && (
                <NotionToolbarFloating
                  enableComments={true}
                  hideFormatting={true}
                  commentActionLabel={commentActionLabel}
                  onAddClaimFromSelection={onAddClaimFromSelection}
                  onAskVera={onAskVera}
                />
              )}
          </EditorContent>
          {!hideEditingTools && <TableBubbleMenu editor={editor} />}
        </div>
      </div>
    </EditorContext.Provider>
  )
}
